import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Search, Inbox as InboxIcon, ChevronLeft, Plus, CheckCircle2,
  ExternalLink, Mail, Phone, MapPin,
} from "lucide-react";
import { Avatar, Button, EmptyState, Skeleton, Input, useToast, Select, Modal, ModalFooter, Label } from "@/components/ui";
import { ChannelIcon, getContactChannels } from "@/features/inbox/ChannelPicker";
import { ContactInboxPanel } from "@/features/inbox/ContactInboxPanel";
import { MessagesSkeleton } from "@/features/inbox/ConversationView";
import {
  PaymentsPanel, TagsPanel, TasksPanel, TimelinePanel,
} from "@/features/contacts/InfoPanels";
import { SmartListPicker } from "@/features/lists/SmartListPicker";
import { useSmartList, smartListQueryParams } from "@/features/lists/SmartListContext";
import { api, ApiError } from "@/lib/api";
import { useUsers } from "@/lib/use-users";
import { formatRelative, formatPhone, fullName } from "@/lib/format";
import { INBOX_FILTER_CHANNELS } from "@/lib/channels";
import type { Contact, Conversation } from "@/lib/types";
import { cn } from "@/lib/utils";

export function InboxPage() {
  const toast = useToast();
  const navigate = useNavigate();
  // /inbox/:contactId deep-links straight into a contact's chat. Empty for /inbox.
  const { contactId: routeContactId } = useParams<{ contactId?: string }>();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  // When set, we're composing a draft message for a contact that doesn't
  // have a conversation yet. Mutually exclusive with activeId.
  const [draftContact, setDraftContact] = useState<Contact | null>(null);
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("open");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const { listId, filter: smartFilter, set: setSmartList } = useSmartList();
  const [composeOpen, setComposeOpen] = useState(false);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  /**
   * Fetch conversation list. `silent=true` skips the loading flag so background
   * polls don't replace the rendered list with a skeleton/empty-state for a
   * frame each tick.
   *
   * Deliberately does NOT decide what's selected. It used to auto-select
   * `conversations[0]` inline, reading `activeId` / `routeContactId` from
   * the closure it was created in — and the 10s poller held onto ONE such
   * closure for as long as the filters didn't change. So a `load` captured
   * while nothing was selected kept believing nothing was selected, and
   * every tick yanked the user to whatever conversation was on top. That's
   * the "I'm reading a message and 10 seconds later it jumps to a different
   * one" bug. Selection now lives in its own effect below, which always
   * reads current state.
   */
  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await api.get<{ conversations: Conversation[] }>("/conversations", {
        channel: channelFilter !== "all" ? channelFilter : undefined,
        status: statusFilter,
        q: debouncedQ || undefined,
        ...smartListQueryParams(listId, smartFilter),
      });
      // Merge the polled list into existing state by preserving rows
      // whose displayed fields haven't moved. Same pattern as
      // ConversationView's message merge — keeps React from
      // re-rendering sidebar rows + the active conversation reference
      // (which would propagate through ChatHeader's fallback prop)
      // when a tick arrives with no actual changes.
      setConversations((prev) => mergeConversations(prev, data.conversations));
    } catch (err) {
      if (!silent) toast.error(err instanceof ApiError ? err.message : "Failed to load inbox");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Always call the LATEST load from the interval. Without this the poller
  // would keep invoking a stale closure over the filter state.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [channelFilter, statusFilter, debouncedQ, listId, smartFilter]);

  // Poll the conversation list every 10s while the page is visible.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (timer) return; timer = setInterval(() => { void loadRef.current(true); }, 10000); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else start(); };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const active = activeId ? conversations.find((c) => c.id === activeId) ?? null : null;

  // Auto-select the first conversation — but ONLY when the user has
  // nothing open. Runs off current state on every render pass, so it can
  // never fight a selection the user made, and it can never re-fire on a
  // poll tick while something is open. Skipped entirely when the URL names
  // a contact; the route effect below owns that case.
  useEffect(() => {
    if (routeContactId) return;
    if (activeId || draftContact) return;
    if (conversations.length === 0) return;
    setActiveId(conversations[0]!.id);
  }, [conversations, routeContactId, activeId, draftContact]);

  // Resolve /inbox/:contactId into either an existing conversation for that
  // contact (most-recent active) or a draft if none exists. Re-runs every
  // time the URL or the conversation list changes — so opening /inbox/abc
  // before conversations have loaded just waits, then snaps in.
  useEffect(() => {
    if (!routeContactId) return;
    // If the active conversation already matches this contact we're good.
    if (active?.contact_id === routeContactId) return;
    if (draftContact?.id === routeContactId) return;

    const existing = conversations
      .filter((c) => c.contact_id === routeContactId)
      .sort((a, b) => +new Date(b.last_message_at ?? b.created_at) - +new Date(a.last_message_at ?? a.created_at))[0];
    if (existing) {
      setDraftContact(null);
      setActiveId(existing.id);
      return;
    }
    // No conversation — drop into draft mode after fetching the contact.
    let cancelled = false;
    api.get<{ contact: Contact }>(`/contacts/${routeContactId}`)
      .then((d) => {
        if (cancelled) return;
        setActiveId(null);
        setDraftContact(d.contact);
      })
      .catch(() => { if (!cancelled) navigate("/inbox", { replace: true }); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [routeContactId, conversations]);

  // Fetch the full contact behind whatever's open on the right pane —
  // either the active conversation OR a draft. Drives the right info panel.
  //
  // Two safety properties this enforces:
  //  1. Hard reset on every target change. Without this, the old contact
  //     + old messages briefly render under the new conversation while
  //     the fetch is in flight (the "double-load with stale message"
  //     bug). Clearing activeContact lets the parent fall through to its
  //     "Loading…" placeholder instead.
  //  2. Identity check on the response. If the resolved contact's id
  //     doesn't match what we asked for (race, redirected fetch, etc.)
  //     we drop it on the floor — better to show "Loading…" than
  //     someone else's data. Pair-validates against URL drift too.
  const [activeContact, setActiveContact] = useState<Contact | null>(null);
  useEffect(() => {
    const targetId = draftContact?.id ?? active?.contact_id ?? null;

    // Hard reset BEFORE any async work — the only safe state for an
    // out-of-date contact is "no contact." Falls through to the
    // "Loading…" branch in the JSX below until the fetch resolves.
    setActiveContact(null);

    if (!targetId) return;
    if (draftContact && draftContact.id === targetId) {
      setActiveContact(draftContact);
      return;
    }
    // If the URL has a contactId, require the target to match it before
    // we even fire the request. Eliminates the "wrong API called for the
    // wrong route" class of bug.
    if (routeContactId && targetId !== routeContactId) return;

    let cancelled = false;
    api.get<{ contact: Contact }>(`/contacts/${targetId}`)
      .then((d) => {
        if (cancelled) return;
        // Identity check: the response must be for the exact contact we
        // asked for, AND must still match the URL if one is set. Rejects
        // any race where targetId changed mid-flight.
        if (d.contact.id !== targetId) return;
        if (routeContactId && d.contact.id !== routeContactId) return;
        setActiveContact(d.contact);
      })
      .catch(() => { /* leave null; UI shows Loading…/empty */ });
    return () => { cancelled = true; };
  }, [active?.contact_id, draftContact, routeContactId]);

  const onCloseConversation = async () => {
    if (!activeId) return;
    setConversations((prev) => prev.filter((c) => c.id !== activeId));
    const wasActive = activeId;
    setActiveId(null);
    // Drop the per-contact URL too. Leaving it behind pointed the route
    // effect at a contact whose thread we just removed from the list, so
    // it fell through to draft-compose for the very conversation the user
    // had just closed. Back at /inbox, the auto-select picks the next one.
    setDraftContact(null);
    navigate("/inbox", { replace: true });
    try {
      await api.patch(`/conversations/${wasActive}`, { status: "closed" });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      load(true);
    }
  };

  // Compose flow: pick a contact → drop straight into the chat. No
  // first-message gate; ConversationView handles the create-on-send.
  const onComposePicked = (c: Contact) => {
    setComposeOpen(false);
    setActiveId(null);
    setDraftContact(c);
    navigate(`/inbox/${c.id}`);
  };

  // Channel filter pills with unread counts.
  const channelCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of conversations) {
      counts.set(c.channel, (counts.get(c.channel) ?? 0) + (c.unread_count > 0 ? 1 : 0));
    }
    return counts;
  }, [conversations]);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Conversation list */}
      <div className={cn(
        "flex w-full md:w-72 lg:w-80 shrink-0 flex-col border-r border-zinc-800/70 bg-zinc-950",
        (active || draftContact) && "hidden md:flex",
      )}>
        <div className="flex h-12 items-center gap-2 border-b border-zinc-800/70 px-3">
          <h1 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <InboxIcon className="h-4 w-4" /> Inbox
          </h1>
          <div className="flex-1" />
          <Button size="icon" variant="ghost" onClick={() => setComposeOpen(true)} aria-label="New message">
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        <div className="border-b border-zinc-800/70 p-2 space-y-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search messages…"
            leftIcon={<Search className="h-3.5 w-3.5" />}
            className="h-8 text-xs"
          />
          <div className="flex items-center gap-1.5">
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-7 text-xs flex-1">
              <option value="open">Open</option>
              <option value="snoozed">Snoozed</option>
              <option value="closed">Closed</option>
              <option value="all">All</option>
            </Select>
            <SmartListPicker
              listId={listId}
              filter={smartFilter}
              onChange={(lid, f) => setSmartList(lid, f)}
            />
          </div>
        </div>

        <div className="border-b border-zinc-800/70 p-1 overflow-x-auto">
          <div className="flex gap-0.5 min-w-max">
            {INBOX_FILTER_CHANNELS.map(({ key, label }) => {
              const unread = channelCounts.get(key) ?? 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setChannelFilter(key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap transition-colors",
                    channelFilter === key ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300"
                  )}
                >
                  {key !== "all" && <ChannelIcon channel={key} />}
                  {label}
                  {unread > 0 && (
                    <span className="inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-semibold text-white">{unread}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-1 p-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : conversations.length === 0 ? (
            <EmptyState
              icon={InboxIcon}
              title="No conversations"
              description="Inbound SMS, emails, and social messages will appear here. Configure your inbound webhook in Settings → Integrations."
              className="m-3"
            />
          ) : (
            <ul>
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setDraftContact(null);
                      setActiveId(c.id);
                      // Push a per-contact URL so the inbox is shareable
                      // and the back button restores the same chat. A
                      // contact-less thread has no URL of its own, so we
                      // clear any stale one — otherwise the route effect
                      // would immediately pull us back to the old contact.
                      navigate(c.contact_id ? `/inbox/${c.contact_id}` : "/inbox");
                    }}
                    className={cn(
                      "flex w-full items-start gap-3 border-b border-zinc-800/40 px-3 py-3 text-left transition-colors",
                      c.id === activeId ? "bg-rose-500/10" : "hover:bg-zinc-900/50"
                    )}
                  >
                    <Avatar name={c.contact_name ?? c.contact_email ?? "?"} src={c.contact_avatar} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className={cn("truncate text-sm", c.unread_count > 0 ? "font-semibold text-zinc-100" : "text-zinc-300")}>
                          {c.contact_name || c.contact_email || c.contact_phone || "Unknown"}
                        </p>
                        <span className="shrink-0 text-[10px] text-zinc-500">{formatRelative(c.last_message_at)}</span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5">
                        <ChannelIcon channel={c.channel} className="text-zinc-500 shrink-0" />
                        <p className={cn("flex-1 truncate text-xs", c.unread_count > 0 ? "text-zinc-300" : "text-zinc-500")}>
                          {c.last_message_preview ?? "(no preview)"}
                        </p>
                        {c.unread_count > 0 && (
                          <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white">{c.unread_count}</span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Active conversation (or draft) — uses the shared <ContactInboxPanel>
          so the chat experience here is byte-for-byte identical to the
          contact-detail page. The header chrome (avatar / name / close)
          is page-specific and stays here. */}
      <div className={cn("flex flex-1 flex-col bg-zinc-950 min-w-0", !(active || draftContact) && "hidden md:flex")}>
        {(active || draftContact) ? (
          // Header + body. The header renders from whatever identity data
          // is available — the full Contact once it resolves, or the
          // conversation row's denormalized name/avatar/email until then.
          // This means the user sees the SAME chrome from click to fully
          // loaded; only the chat body and right rail transition from
          // skeleton to real content.
          <>
            <ChatHeader
              contact={activeContact}
              fallback={
                draftContact
                  ? {
                      id: draftContact.id,
                      name: fullName(draftContact),
                      avatar: draftContact.avatar_url,
                      email: draftContact.email,
                      phone: draftContact.phone,
                    }
                  : active
                    ? {
                        id: active.contact_id ?? "",
                        name: active.contact_name ?? null,
                        avatar: active.contact_avatar ?? null,
                        email: active.contact_email ?? null,
                        phone: active.contact_phone ?? null,
                      }
                    : null
              }
              onBack={() => { setDraftContact(null); setActiveId(null); navigate("/inbox"); }}
              onClose={active ? onCloseConversation : null}
              assigneeId={active?.assignee_id ?? null}
              onAssign={active ? async (userId) => {
                const id = active.id;
                setConversations((prev) => prev.map((c) => c.id === id ? { ...c, assignee_id: userId } : c));
                try {
                  await api.patch(`/conversations/${id}`, { assignee_id: userId });
                } catch (err) {
                  toast.error(err instanceof ApiError ? err.message : "Failed");
                  void load();
                }
              } : null}
            />

            {activeContact ? (
              <ContactInboxPanel
                contact={activeContact}
                initialConversationId={activeId}
                bordered={false}
                onAfterSend={() => void load()}
              />
            ) : (
              // Full contact still loading — show skeleton bubbles in the
              // chat body. Same component ConversationView uses while
              // messages fetch, so once activeContact lands and
              // ContactInboxPanel mounts, the visual transition is
              // skeleton → skeleton → real bubbles (one continuous state).
              <MessagesSkeleton />
            )}
          </>
        ) : (
          <div className="hidden md:flex flex-1 flex-col items-center justify-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-zinc-800/60">
              <InboxIcon className="h-5 w-5 text-zinc-500" />
            </div>
            <p className="mt-3 text-sm text-zinc-400">Pick a conversation to read or reply</p>
            <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} className="mt-4" onClick={() => setComposeOpen(true)}>
              New message
            </Button>
          </div>
        )}
      </div>

      {/* Right info panel — full panel once activeContact resolves;
          skeleton placeholders before so the user sees the destination
          shape immediately on click instead of an empty/jumping rail. */}
      {(active || draftContact) && (
        activeContact ? (
          <ContactInfoPanel contact={activeContact} onContactChange={(c) => setActiveContact(c)} />
        ) : (
          <ContactInfoSkeleton />
        )
      )}

      <ComposePicker open={composeOpen} onClose={() => setComposeOpen(false)} onPicked={onComposePicked} />
    </div>
  );
}

/**
 * Merge polled conversation rows into existing state, preserving
 * references for unchanged rows. React's reconciliation skips
 * re-rendering a row whose props are referentially equal — so the
 * sidebar list and the active-conversation header don't churn on
 * every 10-second tick when nothing actually changed.
 */
function mergeConversations(prev: Conversation[], next: Conversation[]): Conversation[] {
  if (prev.length === 0) return next;
  const prevById = new Map(prev.map((c) => [c.id, c]));
  let changed = prev.length !== next.length;
  const merged = next.map((n, i) => {
    const old = prevById.get(n.id);
    if (!old) { changed = true; return n; }
    const samePos = prev[i]?.id === n.id;
    const sameLastAt = old.last_message_at === n.last_message_at;
    const sameLastPrev = old.last_message_preview === n.last_message_preview;
    const sameUnread = old.unread_count === n.unread_count;
    const sameStatus = old.status === n.status;
    const sameSubject = old.subject === n.subject;
    if (samePos && sameLastAt && sameLastPrev && sameUnread && sameStatus && sameSubject) {
      return old;
    }
    changed = true;
    return n;
  });
  return changed ? merged : prev;
}

/**
 * Right-side rail on the inbox. Identity + reachability are inline (small
 * enough they don't deserve their own component); everything else is the
 * SAME panels the contact-detail page uses on its right rail. Reusing
 * <TagsPanel>, <TasksPanel>, <PaymentsPanel>, <TimelinePanel> here keeps
 * the two pages in lockstep — a fix or polish in one shows up in both.
 */
function ContactInfoPanel({ contact, onContactChange }: {
  contact: Contact;
  onContactChange?: (next: Contact) => void;
}) {
  const channels = useMemo(() => getContactChannels(contact).filter((c) => c.ready), [contact]);
  return (
    <aside className="hidden xl:flex w-80 shrink-0 flex-col gap-3 overflow-y-auto border-l border-zinc-800/70 bg-zinc-950 p-4">
      <div className="flex flex-col items-center text-center pb-2 border-b border-zinc-800/70">
        <Avatar name={fullName(contact)} email={contact.email} src={contact.avatar_url} size="lg" />
        <Link
          to={`/contacts/${contact.id}`}
          state={{ from: "/inbox" }}
          className="mt-2 text-sm font-semibold text-zinc-100 hover:text-rose-300"
        >
          {fullName(contact) || contact.email || "Unnamed"}
        </Link>
        {contact.lifecycle_stage && (
          <p className="mt-0.5 text-[11px] text-zinc-500 capitalize">{contact.lifecycle_stage}</p>
        )}
        <Link to={`/contacts/${contact.id}`} state={{ from: "/inbox" }} className="mt-3 w-full">
          <Button variant="outline" size="sm" className="w-full" leftIcon={<ExternalLink className="h-3 w-3" />}>
            Open contact
          </Button>
        </Link>
      </div>

      {(contact.email || contact.phone || contact.city) && (
        <MiniSection title="Contact">
          {contact.email && (
            <MiniRow icon={Mail}>
              <a href={`mailto:${contact.email}`} className="truncate text-zinc-200 hover:text-rose-300">{contact.email}</a>
            </MiniRow>
          )}
          {contact.phone && (
            <MiniRow icon={Phone}>
              <a href={`tel:${contact.phone}`} className="truncate text-zinc-200 hover:text-rose-300">{formatPhone(contact.phone)}</a>
            </MiniRow>
          )}
          {(contact.city || contact.state || contact.country) && (
            <MiniRow icon={MapPin}>
              <span className="truncate text-zinc-300">{[contact.city, contact.state, contact.country].filter(Boolean).join(", ")}</span>
            </MiniRow>
          )}
        </MiniSection>
      )}

      {/* Social / messaging channels. Email, SMS, and voice live in the
          Contact section above, so we drop them here. Each remaining
          channel renders as a single "[icon] [handle]" row — no label,
          since the icon already conveys the channel. */}
      {(() => {
        const skip = new Set(["email", "sms", "voice", "imessage"]);
        const rows = channels.filter((c) => !skip.has(c.channel));
        if (rows.length === 0) return null;
        return (
          <MiniSection title="Channels">
            <ul className="space-y-1.5 text-xs">
              {rows.map((c) => {
                const isUrl = typeof c.hint === "string" && /^https?:/.test(c.hint);
                return (
                  <li key={c.channel} className="flex items-center gap-2 min-w-0">
                    <c.icon className="h-3 w-3 shrink-0 text-zinc-500" />
                    {isUrl ? (
                      <a href={c.hint} target="_blank" rel="noreferrer" className="truncate text-zinc-200 hover:text-rose-300">{c.hint}</a>
                    ) : (
                      <span className="truncate text-zinc-200">{c.hint}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </MiniSection>
        );
      })()}

      {/* Shared right-rail panels — same exact components as the
          contact-detail page, fully featured (with + buttons and
          inline create/edit dialogs) so the two surfaces behave
          identically. */}
      <TagsPanel contact={contact} onContactChange={onContactChange} />
      <PaymentsPanel contactId={contact.id} />
      <TasksPanel contactId={contact.id} max={5} />
      <TimelinePanel contactId={contact.id} max={6} />
    </aside>
  );
}

/**
 * Chat-area header. Renders identity from the full Contact once it
 * resolves, or from the conversation row's denormalized fields until
 * then — so the user sees a stable header chrome (avatar + name +
 * email) from the moment they click through to fully loaded. The
 * "Open contact" link is the contact's full record; we only enable
 * it when we have a real id so it can't 404 mid-load.
 */
function ChatHeader({
  contact,
  fallback,
  onBack,
  onClose,
  assigneeId,
  onAssign,
}: {
  contact: Contact | null;
  fallback: { id: string; name: string | null; avatar: string | null; email: string | null; phone: string | null } | null;
  onBack: () => void;
  onClose: (() => void) | null;
  /** Who owns this conversation; null = nobody yet. */
  assigneeId?: string | null;
  /** Present for a real conversation (not a draft). null user id = unassign. */
  onAssign?: ((userId: string | null) => void) | null;
}) {
  const { users } = useUsers();
  const id = contact?.id ?? fallback?.id ?? "";
  const name = contact ? fullName(contact) : (fallback?.name ?? null);
  const avatar = contact?.avatar_url ?? fallback?.avatar ?? null;
  const email = contact?.email ?? fallback?.email ?? null;
  const phone = contact?.phone ?? fallback?.phone ?? null;
  const subline = email ?? formatPhone(phone) ?? "No contact info";
  const display = name || email || "Unknown";

  return (
    <div className="flex h-12 items-center gap-3 border-b border-zinc-800/70 px-3 shrink-0">
      <button
        type="button"
        onClick={onBack}
        className="md:hidden inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800/60"
        aria-label="Back"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <Avatar name={name ?? "?"} src={avatar} size="sm" />
      <div className="min-w-0 flex-1">
        {id ? (
          <Link
            to={`/contacts/${id}`}
            state={{ from: "/inbox" }}
            className="block truncate text-sm font-medium text-zinc-100 hover:text-rose-300"
          >
            {display}
          </Link>
        ) : (
          <p className="truncate text-sm font-medium text-zinc-100">{display}</p>
        )}
        <p className="text-xs text-zinc-500 truncate">{subline}</p>
      </div>
      {onAssign && (
        <Select
          value={assigneeId ?? ""}
          onChange={(e) => onAssign(e.target.value || null)}
          title="Who's handling this conversation"
          className="hidden h-7 w-auto max-w-40 text-xs sm:block"
        >
          <option value="">Unassigned</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </Select>
      )}
      {onClose && (
        <Button variant="ghost" size="sm" onClick={onClose} leftIcon={<CheckCircle2 className="h-3.5 w-3.5" />}>
          <span className="hidden sm:inline">Close</span>
        </Button>
      )}
    </div>
  );
}

/**
 * Right-rail skeleton matching the shape of <ContactInfoPanel>. Each
 * section (identity card, contact rows, tags, payments, tasks,
 * timeline) maps 1:1 to a placeholder of the right size so the
 * transition to the loaded panel is a fade-in, not a layout shift.
 */
function ContactInfoSkeleton() {
  return (
    <aside className="hidden xl:flex w-80 shrink-0 flex-col gap-3 overflow-y-auto border-l border-zinc-800/70 bg-zinc-950 p-4">
      {/* Identity card */}
      <div className="flex flex-col items-center text-center pb-2 border-b border-zinc-800/70">
        <Skeleton className="h-14 w-14 rounded-full" />
        <Skeleton className="mt-2 h-4 w-32 rounded" />
        <Skeleton className="mt-2 h-3 w-20 rounded" />
        <Skeleton className="mt-3 h-8 w-full rounded-md" />
      </div>
      {/* Contact section */}
      <div>
        <Skeleton className="mb-2 h-2.5 w-16 rounded" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-40 rounded" />
          <Skeleton className="h-3 w-32 rounded" />
          <Skeleton className="h-3 w-36 rounded" />
        </div>
      </div>
      {/* Tags */}
      <div>
        <Skeleton className="mb-2 h-2.5 w-12 rounded" />
        <div className="flex flex-wrap gap-1.5">
          <Skeleton className="h-5 w-14 rounded-full" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
      {/* Payments / Tasks / Timeline */}
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <Skeleton className="mb-2 h-2.5 w-20 rounded" />
          <div className="space-y-2">
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>
      ))}
    </aside>
  );
}

function MiniSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function MiniRow({ icon: Icon, children }: { icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-xs min-w-0">
      <Icon className="h-3 w-3 shrink-0 text-zinc-500" />
      {children}
    </div>
  );
}

/**
 * Tiny picker dialog that just lets the user choose a contact. No "first
 * message" required — once a contact is picked, the parent drops them
 * straight into the chat area in draft mode.
 */
function ComposePicker({ open, onClose, onPicked }: { open: boolean; onClose: () => void; onPicked: (c: Contact) => void }) {
  const [contactQuery, setContactQuery] = useState("");
  const [contactOptions, setContactOptions] = useState<Contact[]>([]);

  useEffect(() => {
    if (open) {
      setContactQuery("");
      setContactOptions([]);
    }
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!contactQuery.trim()) { setContactOptions([]); return; }
      api.get<{ contacts: Contact[] }>("/contacts", { q: contactQuery, limit: 12 })
        .then((d) => setContactOptions(d.contacts))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [contactQuery]);

  return (
    <Modal open={open} onClose={onClose} title="Start a new chat" size="md">
      <div className="space-y-3">
        <div>
          <Label required>Who do you want to message?</Label>
          <Input
            value={contactQuery}
            onChange={(e) => setContactQuery(e.target.value)}
            placeholder="Search contacts by name, email, or phone…"
            autoFocus
            leftIcon={<Search className="h-3.5 w-3.5" />}
          />
        </div>
        {contactOptions.length > 0 && (
          <ul className="max-h-72 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950">
            {contactOptions.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => onPicked(c)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-900"
                >
                  <Avatar name={fullName(c)} email={c.email} src={c.avatar_url} size="xs" />
                  <span className="truncate text-zinc-200">{fullName(c)}</span>
                  <span className="ml-auto truncate text-xs text-zinc-500">{c.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-zinc-500">
          Pick a contact and we'll drop you straight into the chat — the channel is auto-picked from how they're set up.
        </p>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </ModalFooter>
    </Modal>
  );
}
